'use client';

//import { useCallback, useEffect, useMemo } from 'react';

import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@lichtblick/i18next';
import { DeleteOutline, FilterAlt } from '@lichtblick/icons';
import { TextVariants } from '@lichtblick/theme';
import { AppointmentTypeSelection, CalendarFiltersState, GetCalendarItemsInput, Option } from '@lichtblick/types';

import {
  FilterContainer,
  FilterTriggerContainer,
  IconWrapper,
  ItemWrapper,
  TrashButtonContainer,
} from './AppointmentDrawer.styles';

import { AutocompleteMultiselect, Button, Drawer, Dropdown, SearchInput, Text } from '..';
import { removeSelectedOption } from '../AutocompleteMultiselect/removeSelectedOption';

import { useOptions } from '@/hooks/pm/useOptions';
import { useDebouncedFunction } from '@/hooks/useDebouncedFunction';

export const Filters = ({
  calendarFilters,
  clearAll,
  onFilterSubmitCallback,
  setAppointmentType,
  setBinding,
  setBranch,
  setPartner,
  setPerson,
  setQuery,
  setTeam,
}: {
  calendarFilters: CalendarFiltersState | undefined;
  clearAll: () => void;
  onFilterSubmitCallback: (overrides?: GetCalendarItemsInput, hasPreviousDate?: boolean) => void;
  setAppointmentType: (appointmentType: Option<AppointmentTypeSelection>[]) => void;
  setBinding: (binding?: string) => void;
  setBranch: (branch: Option<string | number | undefined>[]) => void;
  setPartner: (partner: Option<string | number | undefined>[]) => void;
  setPerson: (person: Option<string | number | undefined>[]) => void;
  setQuery: (query: string) => void;
  setTeam: (team: Option<string | number | undefined>[]) => void;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { debouncedFunction: debouncedSetSearch } = useDebouncedFunction();

  const [queryParam] = useQueryState('query', parseAsString);

  const bindingOptions = [
    {
      label: t('pm:bothBinding'),
      value: 'all',
    },
    {
      label: t('pm:isBinding'),
      value: 'binding',
    },
    {
      label: t('pm:isNotBinding'),
      value: 'notBinding',
    },
  ];
  const {
    appointmentTypeOptions,
    craftmanListOptions,
    filteredBranchOptions,
    filteredPartnerOptions,
    filteredTeamOptions,
    setAppointmentTypeQuery,
    setBranchQuery,
    setCraftmanQuery,
    setPartnerQuery,
    setTeamQuery,
  } = useOptions();

  const getFilterCount = () => {
    let value = 0;

    if (calendarFilters?.binding && calendarFilters?.binding !== 'all') {
      value++;
    }

    if (calendarFilters?.branch?.length) {
      value++;
    }

    if (calendarFilters?.appointmentType?.length) {
      value++;
    }

    if (calendarFilters?.team?.length) {
      value++;
    }

    if (calendarFilters?.person?.length) {
      value++;
    }

    if (calendarFilters?.partner?.length) {
      value++;
    }

    return value > 0 ? ` (${value})` : '';
  };

  const memoizedQsQuery = useMemo(() => queryParam, [queryParam]);
  const memoizedReducerQuery = useMemo(() => calendarFilters?.query, [calendarFilters?.query]);

  useEffect(() => {
    if (calendarFilters?.branch && calendarFilters.branch.length > 0 && filteredBranchOptions.length > 0) {
      const updatedBranches = calendarFilters.branch
        .map((branchFilter) => {
          if (branchFilter.label && branchFilter.label !== '') {
            return branchFilter;
          }

          const matchingOption = filteredBranchOptions.find((opt) => opt.value === branchFilter.value);

          return matchingOption || branchFilter;
        })
        .filter((branch) => branch.label && branch.label !== '');

      const hasLabelChanges = updatedBranches.some((updated, index) => {
        const original = calendarFilters.branch?.[index];

        return original && updated.label !== original.label;
      });

      if (hasLabelChanges && updatedBranches.length > 0) {
        setBranch(updatedBranches);
      }
    }
  }, [filteredBranchOptions, calendarFilters?.branch, setBranch]);

  const submitAndClose = (overrides?: GetCalendarItemsInput) => {
    onFilterSubmitCallback(overrides, false);
    setIsOpen(false);
  };

  useEffect(() => {
    if (memoizedReducerQuery === memoizedQsQuery) {
      submitAndClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedReducerQuery, memoizedQsQuery]);

  return (
    <FilterTriggerContainer>
      <ItemWrapper>
        <SearchInput
          onSubmit={(query) => setQuery(query ?? '')}
          placeholder={t('ui.userFilters.searchPlaceholder')}
          value={calendarFilters?.query}
        />
      </ItemWrapper>
      <Drawer
        isActionContainer={false}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        trigger={
          <Button
            icon={<FilterAlt />}
            label={`${t('pm:filters.label')}${getFilterCount()}`}
            onClick={() => setIsOpen(true)}
          ></Button>
        }
      >
        <FilterContainer>
          <ItemWrapper>
            <Text $variant={TextVariants.body.m}>{t('pm:isBinding')}</Text>
            <Dropdown
              defaultValue="all"
              options={bindingOptions}
              placeholder={t('pm:bothBinding')}
              selectedOption={calendarFilters?.binding}
              setSelectedOption={(option) => {
                if (!option) {
                  return;
                }

                setBinding((option as Option)?.value?.toString() ?? '');
              }}
            />
          </ItemWrapper>
          <ItemWrapper>
            <Text $variant={TextVariants.body.m}>{t('pm:branch')}</Text>
            <AutocompleteMultiselect
              onRemoveItem={(branch?: Option) => {
                if (branch) {
                  setBranch(removeSelectedOption(calendarFilters?.branch, branch));
                }
              }}
              onSelect={(e) => {
                setBranch(e ? (e as Option[]) : []);
              }}
              options={[...filteredBranchOptions]}
              setQuery={setBranchQuery}
              values={calendarFilters?.branch ?? []}
            />
          </ItemWrapper>
          <ItemWrapper>
            <Text $variant={TextVariants.body.m}>{t('pm:partner')}</Text>
            <AutocompleteMultiselect
              onRemoveItem={(partner?: Option) => {
                if (partner) {
                  setPartner(removeSelectedOption(calendarFilters?.partner, partner));
                }
              }}
              onSelect={(e) => {
                setPartner(e ? (e as Option[]) : []);
              }}
              options={[...filteredPartnerOptions]}
              setQuery={setPartnerQuery}
              values={calendarFilters?.partner ?? []}
            />
          </ItemWrapper>
          <ItemWrapper>
            <Text $variant={TextVariants.body.m}>{t('pm:appointmentType')}</Text>
            <AutocompleteMultiselect
              onRemoveItem={(type?: Option<AppointmentTypeSelection>) => {
                if (type) {
                  setAppointmentType(removeSelectedOption(calendarFilters?.appointmentType, type));
                }
              }}
              onSelect={(e) => setAppointmentType(e ? e : [])}
              options={appointmentTypeOptions}
              placeholder={t('common:placeholderSelect')}
              setQuery={setAppointmentTypeQuery}
              values={calendarFilters?.appointmentType ?? []}
            />
          </ItemWrapper>
          <ItemWrapper>
            <Text $variant={TextVariants.body.m}>{t('pm:team')}</Text>
            <AutocompleteMultiselect
              onRemoveItem={(team?: Option) => {
                if (team) {
                  setTeam(removeSelectedOption(calendarFilters?.team, team));
                }
              }}
              onSelect={(e) => {
                setTeam(e ? (e as Option[]) : []);
              }}
              options={filteredTeamOptions}
              setQuery={setTeamQuery}
              values={calendarFilters?.team ?? []}
            />
          </ItemWrapper>

          <ItemWrapper>
            <Text $variant={TextVariants.body.m}>{t('pm:person')}</Text>
            <AutocompleteMultiselect
              onRemoveItem={(person?: Option) => {
                if (person) {
                  setPerson(removeSelectedOption(calendarFilters?.person, person));
                }
              }}
              onSelect={(e) => {
                setPerson(e ? (e as Option[]) : []);
              }}
              options={craftmanListOptions}
              setQuery={(e) => debouncedSetSearch(() => setCraftmanQuery(e))}
              values={calendarFilters?.person ?? []}
            />
          </ItemWrapper>

          <TrashButtonContainer>
            <Button label={t('pm:filters.submit')} onClick={() => submitAndClose()}></Button>
            {(calendarFilters?.appointmentType ||
              calendarFilters?.binding ||
              calendarFilters?.branch ||
              calendarFilters?.partner ||
              calendarFilters?.person ||
              calendarFilters?.query ||
              calendarFilters?.team) && (
              <IconWrapper>
                <Button
                  icon={<DeleteOutline />}
                  label={t('pm:filters.clear')}
                  onClick={() => {
                    clearAll();
                    submitAndClose({
                      workflowDefinitions: null,
                      appointmentStates: null,
                      blockerReasons: null,
                      branchOffices: null,
                      endAtUtc: null,
                      isBinding: null,
                      page: null,
                      partnerCompanies: null,
                      startAtUtc: null,
                      teams: null,
                      users: null,
                    });
                  }}
                ></Button>
              </IconWrapper>
            )}
          </TrashButtonContainer>
        </FilterContainer>
      </Drawer>
    </FilterTriggerContainer>
  );
};
